import React, { useEffect } from 'react';
import { WindowLocation } from '@reach/router';
import { useGetPageTitle } from 'helpers/categoryHelpers/useGetPageTitle';
import { useTable, TableInfo } from 'api';
import { Spinner } from 'components/circleSpinner/Spinner';
import { PageHeader } from 'components/categoryHelpers/pageHeader/PageHeader';
import { useMenuState, CrudItems, MenuState } from './CrudMenu';
import { useSaveData } from 'helpers/categoryHelpers/useSaveData';
import { useGetPermissions } from 'helpers/categoryHelpers/useGetPermissions';
import { FlexTable } from 'components/flexTable';
import { FilterBuilder } from 'components/categoryHelpers/filterBuilder/FilterBuilder';
import CrudMenu from './CrudMenu';
import RowOptionsMenu from './RowOptionMenu';
import Pagination from 'components/pagination/Pagination';
import { AxiosRequestConfig } from 'axios';
import { BrandEmptyListImage } from 'components/brandVisual/BrandEmptyListImage';
import { useDispatch, useSelector } from 'react-redux';
import { selectUIUpdate } from 'components/notifications/notificationUIUpdateReducer';
import { useTranslation } from 'react-i18next';
import { TableProps } from '../flexTable/FlexTable';

export type Props<Data> = Omit<Omit<TableProps<Data>, 'table'>, 'rowOptionComponent'> & {
    location: WindowLocation | undefined | string;
    noun: string;
    idColumn: string;
    listEndpointUrl: string;
    crudComponents: CrudItems<Data>;
    hiddenColumns: string[];
    rowOptionComponent?: (menu: any, selectedIds: any[]) => TableProps['rowOptionComponent'];
    noAddBtn: boolean;
    customAddButtons?: (menuActions: MenuState[1]) => React.ReactNode | React.ReactNode[];
    fetchConfig?: AxiosRequestConfig;
    contentTitle?: string;
    classname?: string;
    customPermissions?: string[];

    setSubCatShown?: React.Dispatch<React.SetStateAction<boolean>>;
    headerTitleElement?: JSX.Element | React.ReactElement;

    setSelectedParentData?: React.Dispatch<React.SetStateAction<Data[]>>;
    setIdFromParentRowOption?: React.Dispatch<React.SetStateAction<string | null>>;
    idFromParentRowOption?: string | null;
    selectedParentData?: Data[];
    isParent?: boolean;
    rowButtonBuilder?: (t: TableInfo) => (d: Data) => JSX.Element;
    emptyTableLabel: string;

    notification?: {
        notificationType?: string;
        notificationFn: ({
            pushType,
            customFn,
        }: {
            pushType?: string;
            customFn?: (val?: any) => void;
        }) => void;
    };
};

const permissionsRequested = ['create', 'edit', 'view', 'delete'];

function Category<Data>(props: Props<Data>) {
    const {
        location,
        idColumn,
        listEndpointUrl,
        crudComponents,
        hiddenColumns,
        rowOptionComponent,
        noAddBtn,
        fetchConfig,
        contentTitle,
        classname,
        setSubCatShown,
        setSelectedParentData,
        setIdFromParentRowOption,
        selectedParentData,
        idFromParentRowOption,
        isParent,
        rowButtonBuilder,
        emptyTableLabel,
        customAddButtons,
        customPermissions = [],

        notification,
        ...tableProps
    } = props;

    const { t: tCommon } = useTranslation('common');
    const pageTitle = useGetPageTitle(location);
    const permissions = useGetPermissions(permissionsRequested.concat(customPermissions), location);
    const table = useTable<Data, Error>({
        url: listEndpointUrl,
        ...fetchConfig,
    });

    const { loading, data, error } = table;
    const [selectedIds, setSelectedIds] = React.useState<any[]>([]);
    const changeSearch = data?.actions.changeSearch;

    const [visibleOptions, setVisibleOptions] = React.useState<string | number | null>(null);

    const [transState, setTransState] = React.useState<boolean | null>(null);

    const selectedData = useSaveData<Data>(selectedIds, idColumn, table as TableInfo);

    const [menu, menuActions] = useMenuState<Data>({
        menuItems: crudComponents,
        selectedData,
        permissions,
        table: table as TableInfo,
        idColumn,
    });
    const hasMultiRowAction = crudComponents.some((action) => action.showInMultiSelect);
    useEffect(() => {
        if (setSelectedParentData && setIdFromParentRowOption) {
            if (
                selectedParentData &&
                selectedParentData.length === 0 &&
                idFromParentRowOption === null
            ) {
                setIdFromParentRowOption(menu.selectedId);
                setSelectedParentData(selectedData.length > 0 ? selectedData : menu.selectedData);
            } else if (
                selectedData.length === 0 &&
                menu.selectedData.length === 0 &&
                menu.selectedId === null
            ) {
                setIdFromParentRowOption(null);
                setSelectedParentData([]);
            }
        }
    }, [
        menu.selectedData,
        menu.selectedId,
        selectedData,
        setIdFromParentRowOption,
        setSelectedParentData,
    ]);

    const tableOptionComponent = (data: any) => (
        <RowOptionsMenu
            data={data}
            menuState={[menu, menuActions]}
            selectedIds={selectedIds}
            crudItems={crudComponents}
            idColumn={data[idColumn]}
            visible={visibleOptions}
            setVisible={setVisibleOptions}
        />
    );

    const onActionComplete = () => {
        menuActions.closeMenus();
        setSelectedIds([]);
        table.reload();
    };

    const update = useSelector(selectUIUpdate);
    const dispatch = useDispatch();
    useEffect(() => {
        if (notification && notification.notificationFn && notification.notificationType) {
            notification.notificationFn({
                pushType: notification?.notificationType,
                customFn: () => table?.reload(),
            });
        }
    }, [update, table, dispatch, notification]);

    if (loading && !table.data) return <Spinner />;

    if (error) throw error;

    if (data) {
        const rowButton = rowButtonBuilder
            ? rowButtonBuilder(table as TableInfo)
            : tableProps.rowButton;
        const initialSearchString = data.details.searchString;
        return (
            <div
                className={`CategoryContainer ${
                    transState === true ? 'entered' : transState === false ? 'exited' : ''
                } ${classname ? classname : ''}`}
                style={{ display: 'flex' }}
            >
                <div
                    className={`CategoryPage ${
                        transState === true ? 'entered' : transState === false ? 'exited' : ''
                    }`}
                >
                    <PageHeader
                        noAddBtn={noAddBtn}
                        customAddButtons={
                            customAddButtons ? customAddButtons(menuActions) : undefined
                        }
                        onOpenFilters={() => menuActions.openMenu('FILTERS', null)}
                        addButtonText={`${tCommon('add')} ${props.noun}`}
                        onAddButtonClick={
                            permissions.has('create')
                                ? () => menuActions.openMenu('CREATE', null)
                                : undefined
                        }
                        pageTitle={contentTitle ? contentTitle : pageTitle}
                        table={table as TableInfo}
                        initialSearchString={initialSearchString ?? ''}
                        changeSearch={changeSearch}
                    />
                    <div
                        style={{
                            display: 'flex',
                            flexDirection:
                                table?.data?.details.listData.length === 0 ? 'column' : undefined, //for some reason flex-column breaks the scrolling
                        }}
                    >
                        <FlexTable
                            idColumn={idColumn}
                            table={table as TableInfo}
                            hiddenColumns={hiddenColumns}
                            onFilterClick={
                                data.details.filters.length === 0
                                    ? () => menuActions.openMenu('FILTERS', null)
                                    : () => menuActions.openMenu('FILTERS', null)
                            }
                            checkbox={{
                                selectedIds,
                                setSelectedIds,
                                idColumn,
                                singleRowSelect: !hasMultiRowAction,
                            }}
                            rowOptionComponent={tableOptionComponent}
                            hideFiltering={false}
                            rowButton={rowButton}
                            {...tableProps}
                        />
                        {table?.data?.details.listData.length === 0 && !table?.loading && (
                            <div className="EmptyTable">
                                <BrandEmptyListImage className="EmptyTableImage" alt="MT" />
                                <h3 className="Message">{emptyTableLabel}</h3>
                            </div>
                        )}
                    </div>
                    <Pagination table={table} />
                </div>
                <CrudMenu
                    menu={menu}
                    selectedData={selectedData.length > 0 ? selectedData : menu.selectedData}
                    menuItems={menu.menuItems}
                    crudItems={crudComponents}
                    closePanel={menuActions.closeMenus}
                    onDone={onActionComplete}
                    filters={
                        <FilterBuilder
                            onDone={onActionComplete}
                            table={table as TableInfo}
                            hiddenFields={hiddenColumns}
                        />
                    }
                    setTransState={setTransState}
                    setSubCatShown={setSubCatShown}
                />
            </div>
        );
    }

    return <Spinner />;
}

export default Category;
